import { defineComponent as _defineComponent } from 'vue'
import {computed, ref, onBeforeUnmount} from "vue";
import { TranslateResult } from "vue-i18n";
import i18n from '@/plugins/i18n';
import { debounce, trimStart } from "lodash";
import outboundApiGateway from "@/api/outboundApiGateway";
import {GetPackageContainerResponseType} from "@/api/responses/getPackageContainerResponse";
import { GetPackageResponseType } from '@/api/responses/getPackageResponse';
import { bus } from "@/bootstrap/bus";
import store from "@/bootstrap/store";
import TransferContainerConfirmationModal from "@/components/pages/Outbound/TransferContainerConfirmationModal.vue";
import PackageContainerDetailsCard from '@/components/outbound/PackageContainerDetailsCard.vue';
import { PackageContainerStatus } from '@/enums/PackageContainerTypes';
import NotificationTypes from '@/enums/NotificationTypes';
import auditLog from "@/bootstrap/auditLog";

type ErrorMap = {
  sourceContainer: TranslateResult[];
  sourcePackages: TranslateResult[];
  targetContainer: TranslateResult[];
}

enum TabGroupEnum {
  containers = "containers",
  packages = "packages",
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MoveBetweenContainers',
  setup(__props) {

bus.$on("barcodeScanned", handleScan);

onBeforeUnmount(() => {
  bus.$off("barcodeScanned", handleScan);
})

const sourceContainer = ref<string>('');
const sourcePackages = ref<Array<{name: string, dispatched: boolean}>>([]);
const targetContainer = ref<string>('');
const activeTabGroup = ref<TabGroupEnum>(TabGroupEnum.containers);
const inProgress = ref<boolean>(false);
const errors = ref<ErrorMap>({
  sourceContainer: [],
  targetContainer: [],
  sourcePackages: [],
});
const showConfirmationModal = ref<boolean>(false);
const sourceContainerDetails = ref<GetPackageContainerResponseType|undefined>(undefined);
const targetContainerDetails = ref<GetPackageContainerResponseType|undefined>(undefined);
const sourcePackagesDetails = ref<Array<GetPackageResponseType>>([]);

function handleActiveTabGroup(tabGroup: TabGroupEnum) {
  resetForm();
  activeTabGroup.value = tabGroup;
}

const isSourceContainerValid = computed(() => {
  return (
    !!sourceContainerDetails.value &&
    errors.value.sourceContainer.length === 0
  )
})

const isSourcePackagesValid = computed(() => {
  return (
    sourcePackages.value.length > 0 &&
    errors.value.sourcePackages.length === 0
  )
})

const isTargetValid = computed(() => {
  return (
    !!targetContainerDetails.value &&
    errors.value.targetContainer.length === 0
  )
})

const canTransferContainer = computed(() => {
  return ( isSourceContainerValid.value && isTargetValid.value)
});

const canTransferPackagesToContainer = computed(() => {
  return isTargetValid.value && isSourcePackagesValid.value;
})

async function handleScan(scanResult: string): Promise<void> {

  if(activeTabGroup.value === TabGroupEnum.containers) {
    if(!targetContainer.value && !sourceContainer.value) {
      auditLog.moveBetweenPackageContainerStarted()
    }
    // all SOURCE validations must pass before TARGET container validations
    if(!sourceContainer.value || errors.value.sourceContainer.length > 0) {
      sourceContainer.value = scanResult;
      await getPackageContainerDetails('source');
    } else {
      targetContainer.value = scanResult;
      await getPackageContainerDetails('target');
    }
  }

  // For "package" TabGroup:
  // if scan starts with PC then this is a container. This may cause issues if Tracking code starts with "PC" (but we have not yet seen instances of this)
  // everything else is assumed to a package
  if(activeTabGroup.value === TabGroupEnum.packages) {
    if(!targetContainer.value && sourcePackages.value.length === 0) {
      auditLog.assignPackagesToPackageContainerStarted(scanResult)
    }

    if(isValidPackageContainer(scanResult)) {
      targetContainer.value = scanResult;
      await getPackageContainerDetails('target');
    } else {
      if(isDuplicatePackage(scanResult)) {
        store.dispatch("core/addNotification", {
          message: i18n.t('outbound.duplicate_package_scanned'),
          type: NotificationTypes.NOTIFICATION_TYPE_INFO,
          timeout: 1000,
        });
        return;
      }
      await getPackageDetails(scanResult);
    }
  }
}

const isValidPackageContainer = (container: string): boolean => {
  return container?.startsWith('PC')
      && container.length > 2
      && !isNaN(Number(trimStart(container, 'PC')))
}

const isDuplicatePackage = (p: string): boolean => {
  return !!sourcePackages.value.find((pack) => pack.name === p);
}

function resetForm(): void {
  sourceContainer.value = '';
  sourcePackages.value = [];
  targetContainer.value = '';
  sourceContainerDetails.value = undefined;
  targetContainerDetails.value = undefined;
  sourcePackagesDetails.value = [];
  errors.value = { sourceContainer: [], targetContainer: [], sourcePackages: [] };
  if(activeTabGroup.value === TabGroupEnum.packages) {
    auditLog.assignPackagesToPackageContainerComplete([], 'reset')
  } else {
    auditLog.moveBetweenPackageContainerComplete('', 'reset')
  }
}

const getPackageContainerDetails = async (containerType: 'source' | 'target'): Promise<void> => {
  const containerTextInput = containerType === 'source' ? sourceContainer.value : targetContainer.value;
  const containerDetails = containerType === 'source' ? sourceContainerDetails : targetContainerDetails;
  const errorKey = containerType === 'source' ? 'sourceContainer' : 'targetContainer';

  // reset container details
  containerDetails.value = undefined;

  if (!containerTextInput) {
    errors.value[errorKey] = [];
    return;
  }

  if (!isValidPackageContainer(containerTextInput)) {
    errors.value[errorKey] = [i18n.t(`outbound.transfer.errors.invalid_container_type`)];
    auditLog.invalidPackageContainerScanned(containerTextInput, 'invalid');
    return;
  } else {
    errors.value[errorKey] = [];
  }

  if(containersMatch.value) {
    errors.value.targetContainer = [i18n.t('outbound.transfer.errors.same_container')];
    auditLog.invalidPackageContainerScanned(containerTextInput, 'same as source');
    return;
  }

  try {
    const containerDetailsResponse = await outboundApiGateway.getPackageContainer(trimStart(containerTextInput, 'PC'));
    if(containerDetailsResponse.success === false) {
      store.dispatch("core/addNotification", {
        message: i18n.t('outbound.transfer.notifications.api_validaton_error'),
        type: NotificationTypes.NOTIFICATION_TYPE_ERROR,
        timeout: 3500,
      });
      auditLog.moveBetweenPackageContainerWarning(containerTextInput, 'API response validation error');
    } else {
      containerDetails.value = containerDetailsResponse.data;
    }
  } catch (e) {
    errors.value[errorKey] = [i18n.t(`outbound.transfer.errors.container_not_found`)];
    return;
  }

  if (
      containerType === 'source'
      && containerDetails.value!== undefined
      && isContainerEmpty(containerDetails.value)
  ) {
    errors.value[errorKey] = [i18n.t(`outbound.transfer.errors.container_empty`)];
    auditLog.invalidPackageContainerScanned(containerTextInput, 'empty');
    return;
  }
  if (containerDetails.value!== undefined && isContainerDispatched(containerDetails.value)) {
    errors.value[errorKey] = [i18n.t(`outbound.transfer.errors.container_dispatched`)];
    auditLog.invalidPackageContainerScanned(containerTextInput, 'already dispatched');
    return;
  }

  const scanResult = containerType === 'source' ? sourceContainer.value : targetContainer.value;
  auditLog.packageContainerScanned(scanResult, containerType);
}

const getPackageDetails = async(trackingCode: string): Promise<void> => {
  try {
    const packageDetailResponse = await outboundApiGateway.getPackage(trackingCode);
    if(packageDetailResponse.success === false) {
      store.dispatch("core/addNotification", {
        message: i18n.t('outbound.transfer.notifications.api_validaton_error'),
        type: NotificationTypes.NOTIFICATION_TYPE_ERROR,
        timeout: 3500,
      });
      auditLog.packageToPackageContainerWarning(trackingCode, 'API response validation error');
    } else {
      const packageDetail = packageDetailResponse.data;
      const isDispatchedAlready = packageDetail.included ? packageDetail.included[0].attributes.status === PackageContainerStatus.DISPATCHED : false ;
      if(isDispatchedAlready) {
        auditLog.packageToPackageContainerWarning(trackingCode, 'package dispatched already');
      }
      sourcePackagesDetails.value.push(packageDetail);
      sourcePackages.value.push({
        name: trackingCode,
        dispatched: isDispatchedAlready
      });
      auditLog.packageToPackageContainerScanned(trackingCode);
    }
  } catch (e) {
    errors.value['sourcePackages'] = [i18n.t(`outbound.package_not_found`)];
    return;
  }
}

const getSourceContainerDetails = async (): Promise<void> => {
  await getPackageContainerDetails('source');
}

const getTargetContainerDetails = async (): Promise<void> => {
  await getPackageContainerDetails('target');
}

const removePackage = (p: string): void => {
  sourcePackages.value.splice(sourcePackages.value.findIndex((pack) => pack.name === p), 1)

  // if there are no more packages this is similar to reset
  if(sourcePackages.value.length === 0) {
    auditLog.assignPackagesToPackageContainerComplete([], 'removed last package');
  }
}

const debounceGetSourceContainerDetails = debounce(getSourceContainerDetails, 400);
const debounceGetTargetContainerDetails = debounce(getTargetContainerDetails, 400);

const containersMatch = computed(() => {
  return sourceContainer.value === targetContainer.value
});

const isContainerEmpty = ((container: GetPackageContainerResponseType): boolean => {
  return container.data.relationships?.packages.data?.length === 0;
});

const isContainerDispatched = ((container: GetPackageContainerResponseType): boolean => {
  return container.data.attributes.status === PackageContainerStatus.DISPATCHED;
})

const openConfirmationModal = async (): Promise<void> => {

  if(activeTabGroup.value === TabGroupEnum.containers) {
    if(canTransferContainer) {
      if(sourceContainerDetails.value)
      showConfirmationModal.value = true;
    }
  }

  if(activeTabGroup.value === TabGroupEnum.packages) {
    if(canTransferPackagesToContainer) {
      showConfirmationModal.value = true;
    }
  }

}

const closeConfirmationModal = ():void => {
  showConfirmationModal.value = false;
}

function submitAndCloseConfirmationModal(): void {
  if(activeTabGroup.value === TabGroupEnum.containers) {
    transferContainer();
  } else if (activeTabGroup.value === TabGroupEnum.packages) {
    assignPackagesToContainer();
  }
  closeConfirmationModal();
}

async function transferContainer(): Promise<void> {
  inProgress.value = true;
  try {
    const response = await outboundApiGateway.transferPackageContainer({
      sourceContainer: trimStart(sourceContainer.value, 'PC'),
      targetContainer: trimStart(targetContainer.value, 'PC'),
    });
    auditLog.moveBetweenPackageContainerComplete(sourceContainer.value, targetContainer.value);
    store.dispatch("core/addNotification", {
      message: i18n.t('outbound.transfer.notifications.transfer_success', {numberOfParcels: response.data.meta.updated}),
      type: NotificationTypes.NOTIFICATION_TYPE_SUCCESS,
      timeout: 3500,
    });
    resetForm();
  } catch (e) {
    console.error(e);
    store.dispatch("core/addNotification", {
      message: i18n.t(`outbound.transfer.notifications.transfer_failed`),
      type: NotificationTypes.NOTIFICATION_TYPE_ERROR,
      timeout: 3500,
    });
  } finally {
    inProgress.value = false;
  }
}

async function assignPackagesToContainer(): Promise<void> {
  inProgress.value = true;

  const packageDetails = sourcePackagesDetails.value.map((pkg) => {
    return {
      tracking_code: pkg.data.attributes.tracking_code,
      order_id: pkg.data.attributes.order_id
    };
  });

  try {
    const response = await outboundApiGateway.assignToPackageContainer({
      packages: packageDetails.map((pkg) => pkg.tracking_code),
      targetContainer: trimStart(targetContainer.value, 'PC'),
    })
    auditLog.assignPackagesToPackageContainerComplete(packageDetails, targetContainer.value);
    store.dispatch("core/addNotification", {
      message: i18n.t(`Assigned ${response.data.meta.updated} packages sucessfully`),
      type: NotificationTypes.NOTIFICATION_TYPE_SUCCESS,
      timeout: 3500,
    });
    resetForm();
  } catch (e) {
    console.error(e);
    store.dispatch("core/addNotification", {
      message: i18n.t(`Unable to assign packages`),
      type: NotificationTypes.NOTIFICATION_TYPE_ERROR,
      timeout: 3500,
    });
  } finally {
    inProgress.value = false;
  }
}

const numberOfPackagesToMove = computed( () => {
  if(activeTabGroup.value === TabGroupEnum.containers) {
    return sourceContainerDetails.value
        ? sourceContainerDetails.value.data.relationships?.packages.data?.length
        : 0;
  }
  return sourcePackages.value.length
})


return { __sfc: true,TabGroupEnum, sourceContainer, sourcePackages, targetContainer, activeTabGroup, inProgress, errors, showConfirmationModal, sourceContainerDetails, targetContainerDetails, sourcePackagesDetails, handleActiveTabGroup, isSourceContainerValid, isSourcePackagesValid, isTargetValid, canTransferContainer, canTransferPackagesToContainer, handleScan, isValidPackageContainer, isDuplicatePackage, resetForm, getPackageContainerDetails, getPackageDetails, getSourceContainerDetails, getTargetContainerDetails, removePackage, debounceGetSourceContainerDetails, debounceGetTargetContainerDetails, containersMatch, isContainerEmpty, isContainerDispatched, openConfirmationModal, closeConfirmationModal, submitAndCloseConfirmationModal, transferContainer, assignPackagesToContainer, numberOfPackagesToMove, trimStart, TransferContainerConfirmationModal, PackageContainerDetailsCard }
}

})